/**
 * AgentPanel - Panel para visualizar y gestionar agentes locales conectados
 * Muestra agentes registrados, estado, dispositivos descubiertos y permite enviar comandos
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Server,
  Wifi,
  WifiOff,
  RefreshCw,
  Monitor,
  Clock,
  Cpu,
  Radio,
  ChevronDown,
  ChevronRight,
  Trash2,
  Play,
  Search,
  Download,
  Copy,
  Check,
  Terminal,
  Globe,
  Loader2,
  Apple,
  MonitorSmartphone,
  Laptop,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

// Windows icon SVG
const WindowsIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/>
  </svg>
);

// Apple icon
const AppleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

export default function AgentPanel({ onDevicesUpdate }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAgents, setExpandedAgents] = useState({});
  const [sendingCommand, setSendingCommand] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloadingPlatform, setDownloadingPlatform] = useState(null);

  // Fetch agents
  const fetchAgents = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/rf-network/agents`, getAuthHeaders());
      setAgents(response.data.agents || []);
    } catch (error) {
      // Silent fail - agents might not exist yet
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
    // Poll every 10 seconds
    const interval = setInterval(fetchAgents, 10000);
    return () => clearInterval(interval);
  }, [fetchAgents]);

  // Toggle agent expansion
  const toggleAgent = (agentId) => {
    setExpandedAgents(prev => ({
      ...prev,
      [agentId]: !prev[agentId]
    }));
  };

  // Send command to agent
  const sendCommand = async (agentId, commandType, params = {}) => {
    setSendingCommand(`${agentId}-${commandType}`);
    try {
      await axios.post(
        `${API_BASE}/api/rf-network/agent/send-command/${agentId}`,
        { type: commandType, params },
        getAuthHeaders()
      );
      toast.success(`Comando "${commandType}" enviado al agente`);
      
      // Refresh after rescan
      if (commandType === 'rescan') {
        setTimeout(() => {
          fetchAgents();
          if (onDevicesUpdate) onDevicesUpdate();
        }, 3000);
      }
    } catch (error) {
      toast.error('Error enviando comando');
    } finally {
      setSendingCommand(null);
    }
  };

  // Download visual installer with pre-embedded token
  const downloadInstaller = async (platform) => {
    setDownloadingPlatform(platform);
    try {
      // Use the new visual installers with pre-embedded token
      const endpoint = platform === 'windows' 
        ? '/api/agent/download/visual/windows'
        : '/api/agent/download/visual/mac';
      
      const response = await axios.get(`${API_BASE}${endpoint}`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Create download link with correct extension
      // Mac downloads as ZIP to preserve permissions
      const blob = new Blob([response.data], { 
        type: platform === 'windows' ? 'application/hta' : 'application/zip' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = platform === 'windows' 
        ? 'AVX_RF_Agent_Setup.hta' 
        : 'AVX_RF_Agent_Setup_Mac.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      if (platform === 'mac') {
        toast.success('Instalador Mac descargado. Descomprime el ZIP y haz doble clic en el archivo.');
      } else {
        toast.success('Instalador Windows descargado. ¡Solo haz doble clic para instalar!');
      }
    } catch (error) {
      toast.error('Error descargando instalador');
    } finally {
      setDownloadingPlatform(null);
    }
  };

  // Download quick-start with embedded token
  const downloadQuickStart = async (platform) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('No hay sesión activa');
      return;
    }
    
    setDownloadingPlatform(`quick-${platform}`);
    try {
      const response = await axios.get(
        `${API_BASE}/api/agent/download/quick-start?token=${token}&platform=${platform}`,
        { responseType: 'blob' }
      );
      
      const ext = platform === 'windows' ? 'bat' : (platform === 'mac' ? 'command' : 'sh');
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AVX_RF_QuickStart.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Quick Start descargado. Ejecútalo para conectar inmediatamente.');
    } catch (error) {
      toast.error('Error descargando Quick Start');
    } finally {
      setDownloadingPlatform(null);
    }
  };

  // Copy token to clipboard
  const copyToken = () => {
    const token = localStorage.getItem('token');
    if (token) {
      navigator.clipboard.writeText(token);
      setCopied(true);
      toast.success('Token copiado al portapapeles');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Format relative time
  const formatRelativeTime = (isoDate) => {
    if (!isoDate) return 'Nunca';
    const date = new Date(isoDate);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);

    if (diffSec < 60) return 'Hace unos segundos';
    if (diffMin < 60) return `Hace ${diffMin} min`;
    if (diffHour < 24) return `Hace ${diffHour} horas`;
    return date.toLocaleDateString('es-CL');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#34C759]" />
        <span className="ml-2 text-[#71717A]">Cargando agentes...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Server className="w-5 h-5 text-[#34C759]" />
          <h3 className="text-lg font-semibold text-[#F4F4F5]">Agentes Locales</h3>
          <Badge variant="outline" className="ml-2">
            {agents.length} {agents.length === 1 ? 'agente' : 'agentes'}
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowInstructions(true)}
            className="text-xs bg-[#34C759]/10 border-[#34C759]/30 text-[#34C759] hover:bg-[#34C759]/20"
            data-testid="show-agent-instructions-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            Descargar Agente
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAgents}
            data-testid="refresh-agents-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Agents List */}
      {agents.length === 0 ? (
        <Card className="bg-[#111111] border-white/10">
          <CardContent className="py-12 text-center">
            <Server className="w-12 h-12 mx-auto text-[#52525B] mb-4" />
            <h4 className="text-lg font-medium text-[#F4F4F5] mb-2">
              No hay agentes conectados
            </h4>
            <p className="text-sm text-[#71717A] mb-6 max-w-md mx-auto">
              Los agentes locales permiten detectar y controlar equipos RF 
              (Shure, Sennheiser) que están en tu red local.
            </p>
            
            {/* Quick download buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => setShowInstructions(true)}
                className="bg-[#34C759] hover:bg-[#2DB34C] text-black"
                data-testid="setup-first-agent-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Descargar Instalador
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <ScrollArea className="h-[400px]">
          <div className="space-y-3">
            {agents.map(agent => (
              <Collapsible
                key={agent.agent_id}
                open={expandedAgents[agent.agent_id]}
                onOpenChange={() => toggleAgent(agent.agent_id)}
              >
                <Card className="bg-[#111111] border-white/10 overflow-hidden">
                  <CollapsibleTrigger className="w-full" data-testid={`agent-${agent.agent_id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {expandedAgents[agent.agent_id] ? (
                            <ChevronDown className="w-4 h-4 text-[#71717A]" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-[#71717A]" />
                          )}
                          
                          {/* Status indicator */}
                          <div className={`w-3 h-3 rounded-full ${
                            agent.status === 'online' ? 'bg-[#34C759]' : 'bg-[#FF3B30]'
                          }`} />
                          
                          <div className="text-left">
                            <h4 className="font-medium text-[#F4F4F5] flex items-center gap-2">
                              <Monitor className="w-4 h-4 text-[#71717A]" />
                              {agent.hostname || agent.agent_id}
                            </h4>
                            <p className="text-xs text-[#71717A]">
                              {agent.subnet}.* • v{agent.version || '1.0'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4 text-right">
                          <div>
                            <p className="text-sm font-medium text-[#F4F4F5]">
                              {agent.devices_count || 0} dispositivos
                            </p>
                            <p className="text-xs text-[#71717A]">
                              {formatRelativeTime(agent.last_seen)}
                            </p>
                          </div>
                          <Badge 
                            variant={agent.status === 'online' ? 'default' : 'destructive'}
                            className={agent.status === 'online' 
                              ? 'bg-[#34C759]/20 text-[#34C759] border-[#34C759]/30' 
                              : ''
                            }
                          >
                            {agent.status === 'online' ? (
                              <><Wifi className="w-3 h-3 mr-1" /> Online</>
                            ) : (
                              <><WifiOff className="w-3 h-3 mr-1" /> Offline</>
                            )}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <CardContent className="pt-0 pb-4 border-t border-white/5 mt-2">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 pt-4">
                        <div className="bg-[#0A0A0A] rounded p-3">
                          <p className="text-xs text-[#71717A] mb-1">Subnet</p>
                          <p className="font-mono text-sm text-[#F4F4F5]">{agent.subnet}.*</p>
                        </div>
                        <div className="bg-[#0A0A0A] rounded p-3">
                          <p className="text-xs text-[#71717A] mb-1">Versión</p>
                          <p className="font-mono text-sm text-[#F4F4F5]">{agent.version || '1.0.0'}</p>
                        </div>
                        <div className="bg-[#0A0A0A] rounded p-3">
                          <p className="text-xs text-[#71717A] mb-1">Registrado</p>
                          <p className="text-sm text-[#F4F4F5]">
                            {agent.registered_at 
                              ? new Date(agent.registered_at).toLocaleDateString('es-CL')
                              : 'N/A'
                            }
                          </p>
                        </div>
                        <div className="bg-[#0A0A0A] rounded p-3">
                          <p className="text-xs text-[#71717A] mb-1">Última Actividad</p>
                          <p className="text-sm text-[#F4F4F5]">{formatRelativeTime(agent.last_seen)}</p>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => sendCommand(agent.agent_id, 'rescan')}
                          disabled={agent.status !== 'online' || sendingCommand === `${agent.agent_id}-rescan`}
                          data-testid={`rescan-agent-${agent.agent_id}`}
                        >
                          {sendingCommand === `${agent.agent_id}-rescan` ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Search className="w-4 h-4 mr-1" />
                          )}
                          Re-escanear Red
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-[#71717A] hover:text-[#F4F4F5]"
                          disabled={agent.status !== 'online'}
                        >
                          <Radio className="w-4 h-4 mr-1" />
                          Ver Dispositivos
                        </Button>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}
          </div>
        </ScrollArea>
      )}

      {/* Download Dialog */}
      <Dialog open={showInstructions} onOpenChange={setShowInstructions}>
        <DialogContent className="max-w-2xl bg-[#111111] border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#F4F4F5]">
              <Download className="w-5 h-5 text-[#34C759]" />
              Descargar Agente Local
            </DialogTitle>
            <DialogDescription>
              Selecciona tu sistema operativo para descargar el instalador
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="download" className="mt-4">
            <TabsList className="grid w-full grid-cols-2 bg-[#0A0A0A]">
              <TabsTrigger value="download" className="data-[state=active]:bg-[#34C759] data-[state=active]:text-black">
                Descarga Rápida
              </TabsTrigger>
              <TabsTrigger value="manual" className="data-[state=active]:bg-[#34C759] data-[state=active]:text-black">
                Instalación Manual
              </TabsTrigger>
            </TabsList>
            
            {/* Quick Download Tab */}
            <TabsContent value="download" className="space-y-4 mt-4">
              <div className="bg-[#1A1A1A] rounded-lg p-4 border border-[#34C759]/30">
                <p className="text-sm text-[#34C759] font-medium flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Tu cuenta ya está configurada. Solo descarga e instala.
                </p>
              </div>
              
              {/* Platform Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Windows */}
                <Card className="bg-[#0A0A0A] border-white/10 hover:border-[#00A4EF]/50 transition-colors cursor-pointer group"
                      onClick={() => downloadInstaller('windows')}>
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#00A4EF]/10 flex items-center justify-center group-hover:bg-[#00A4EF]/20 transition-colors">
                      <WindowsIcon />
                    </div>
                    <h4 className="font-semibold text-[#F4F4F5] mb-1">Windows</h4>
                    <p className="text-xs text-[#71717A] mb-4">Windows 10/11 - Doble clic</p>
                    <Button 
                      className="w-full bg-[#00A4EF] hover:bg-[#00A4EF]/90 text-white"
                      disabled={downloadingPlatform === 'windows'}
                      onClick={(e) => { e.stopPropagation(); downloadInstaller('windows'); }}
                      data-testid="download-windows-installer"
                    >
                      {downloadingPlatform === 'windows' ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Descargar Instalador
                    </Button>
                  </CardContent>
                </Card>
                
                {/* macOS */}
                <Card className="bg-[#0A0A0A] border-white/10 hover:border-[#A2AAAD]/50 transition-colors cursor-pointer group"
                      onClick={() => downloadInstaller('mac')}>
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#A2AAAD]/10 flex items-center justify-center group-hover:bg-[#A2AAAD]/20 transition-colors">
                      <AppleIcon />
                    </div>
                    <h4 className="font-semibold text-[#F4F4F5] mb-1">macOS</h4>
                    <p className="text-xs text-[#71717A] mb-4">Intel, Apple Silicon</p>
                    <Button 
                      className="w-full bg-[#A2AAAD] hover:bg-[#A2AAAD]/90 text-black"
                      disabled={downloadingPlatform === 'mac'}
                      onClick={(e) => { e.stopPropagation(); downloadInstaller('mac'); }}
                      data-testid="download-mac-installer"
                    >
                      {downloadingPlatform === 'mac' ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Descargar .zip
                    </Button>
                  </CardContent>
                </Card>
              </div>
              
              {/* Instructions */}
              <div className="bg-[#0A0A0A] rounded-lg p-4 border border-white/5">
                <h4 className="font-medium text-[#F4F4F5] mb-3">Instrucciones</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#00A4EF] font-medium mb-2 flex items-center gap-2">
                      <WindowsIcon /> Windows
                    </p>
                    <ol className="text-[#71717A] space-y-1 list-decimal list-inside">
                      <li>Descarga el instalador .hta</li>
                      <li>Haz doble clic en el archivo</li>
                      <li>Clic en "Instalar Agente"</li>
                    </ol>
                  </div>
                  <div>
                    <p className="text-[#A2AAAD] font-medium mb-2 flex items-center gap-2">
                      <AppleIcon /> macOS
                    </p>
                    <ol className="text-[#71717A] space-y-1 list-decimal list-inside">
                      <li>Descarga y descomprime el ZIP</li>
                      <li>Haz doble clic en el archivo .command</li>
                      <li>Si pide permisos: clic derecho → Abrir</li>
                    </ol>
                  </div>
                </div>
              </div>
              
              {/* Supported devices */}
              <div className="bg-[#0A0A0A] rounded-lg p-4 border border-white/5">
                <h4 className="font-medium text-[#F4F4F5] mb-3 flex items-center gap-2">
                  <Radio className="w-4 h-4 text-[#34C759]" />
                  Dispositivos soportados
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#E31837] font-medium mb-1">Shure (completo)</p>
                    <p className="text-[#71717A]">AD4Q, AD4D, ULXD4, QLXD4</p>
                  </div>
                  <div>
                    <p className="text-[#00A0E3] font-medium mb-1">Sennheiser (básico)</p>
                    <p className="text-[#71717A]">EM 2050, EM 6000, EW-D/DX</p>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            {/* Manual Installation Tab */}
            <TabsContent value="manual" className="space-y-4 mt-4">
              {/* Step 1 */}
              <div className="bg-[#0A0A0A] rounded-lg p-4">
                <h4 className="font-medium text-[#F4F4F5] mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#34C759] text-black text-sm flex items-center justify-center">1</span>
                  Instalar Python y dependencias
                </h4>
                <pre className="bg-[#1A1A1A] rounded p-3 font-mono text-sm text-[#A1A1AA] overflow-x-auto">
                  pip install aiohttp websockets
                </pre>
              </div>
              
              {/* Step 2 */}
              <div className="bg-[#0A0A0A] rounded-lg p-4">
                <h4 className="font-medium text-[#F4F4F5] mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#34C759] text-black text-sm flex items-center justify-center">2</span>
                  Copiar tu token
                </h4>
                <div className="flex gap-2">
                  <div className="flex-1 bg-[#1A1A1A] rounded p-3 font-mono text-xs text-[#A1A1AA] overflow-hidden">
                    <span className="opacity-50">{localStorage.getItem('token')?.substring(0, 50)}...</span>
                  </div>
                  <Button 
                    onClick={copyToken}
                    variant="outline"
                    className="shrink-0"
                    data-testid="copy-token-btn"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-[#34C759]" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Step 3 */}
              <div className="bg-[#0A0A0A] rounded-lg p-4">
                <h4 className="font-medium text-[#F4F4F5] mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#34C759] text-black text-sm flex items-center justify-center">3</span>
                  Ejecutar el agente
                </h4>
                <pre className="bg-[#1A1A1A] rounded p-3 font-mono text-sm text-[#A1A1AA] overflow-x-auto">
{`# Descargar
curl -O ${API_BASE}/api/agent/download/script

# Ejecutar
python avx_agent.py -t TU_TOKEN`}
                </pre>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInstructions(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
